import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getVideoEditingModels } from '../services/aiService';
import { getModelForCapability } from '../utils/modelHelper';
import { getResolutionOptionsForModel, getDefaultResolutionForModel, getResolutionCreditsForModel } from '../utils/videoModelResolutions';

const toOption = (m) => ({ id: m.key || m.id || m.name, name: m.name || m.key || m.id, provider: m.provider || '', disabled: !!m.disabled, resolutions: m.resolutions || null, default_resolution: m.default_resolution || null });

// Model + resolution picker for the "Video editing" capability.
export const EditModelSettings = ({ model, setModel, resolution, setResolution, disabled, children }) => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        getVideoEditingModels().then(list => {
            if (!alive) return;
            const opts = list.map(toOption).filter((m, i, a) => m.id && a.findIndex(x => x.id === m.id) === i);
            const def = (getModelForCapability('video_editing') || getModelForCapability('Video editing') || '').toLowerCase();
            const di = def ? opts.findIndex(m => m.id.toLowerCase() === def || m.name.toLowerCase() === def) : -1;
            if (di > 0) opts.unshift(opts.splice(di, 1)[0]);
            setModels(opts);
            setLoading(false);
            if (!model) {
                const first = opts.find(m => !m.disabled);
                if (first) setModel(first.id);
            }
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const modelObj = useMemo(() => models.find(m => m.id === model) || model, [models, model]);
    const resOptions = useMemo(() => (model ? getResolutionOptionsForModel(modelObj) : []), [model, modelObj]);
    const resCredits = useMemo(() => getResolutionCreditsForModel(modelObj), [modelObj]);

    const resKeyRef = useRef(null);
    useEffect(() => {
        if (!model) return;
        // New model / catalog data → catalog default; otherwise keep the user's pick.
        const def = getDefaultResolutionForModel(modelObj);
        const key = `${model}|${def}|${resOptions.join(',')}`;
        const changed = resKeyRef.current !== key;
        resKeyRef.current = key;
        setResolution(prev => (!changed && resOptions.includes(prev) ? prev : def));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model, resOptions]);

    const sel = 'w-full mt-1 p-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-200 focus:border-purple-500 focus:outline-none';
    return (
        <div className="grid grid-cols-2 gap-2 p-2.5 rounded-lg bg-gray-800/40 border border-gray-700/80" data-testid="edit-model-settings">
            <div className="col-span-2">
                <label className="text-[11px] font-medium text-gray-400">AI model (Video editing)</label>
                <select value={model || ''} onChange={e => setModel(e.target.value)} disabled={disabled || loading || models.length === 0}
                    className={sel} data-testid="edit-model-select">
                    {loading && <option value="">Loading models…</option>}
                    {!loading && models.length === 0 && <option value="">AI Toolkit default</option>}
                    {models.map(m => <option key={m.id} value={m.id} disabled={m.disabled}>{m.name}{m.provider ? ` (${m.provider})` : ''}</option>)}
                </select>
            </div>
            <div className="col-span-2">
                <label className="text-[11px] font-medium text-gray-400">Resolution</label>
                <select value={resolution || ''} onChange={e => setResolution(e.target.value)} disabled={disabled || resOptions.length === 0}
                    className={sel} data-testid="edit-resolution-select">
                    {resOptions.length === 0 && <option value="">Model default</option>}
                    {resOptions.map(r => <option key={r} value={r}>{r}{resCredits[r] != null ? ` · ${resCredits[r]} credits/s` : ''}</option>)}
                </select>
            </div>
            {children}
        </div>
    );
};
